import { useMemo } from "react"

import { useBaselineDetail, useBaselineHistory } from "@/metrics/baselines"

import type { BucketInterval } from "@/utils/date/timeBucketing"

import type { ChartWidgetConfig } from "../../types/chart"
import { AGGREGATION_TYPE } from "../../types/core"
import { createChartBaselineData } from "../../utils/chart/chartWidgetState"

export function useChartBaselines(
  config: ChartWidgetConfig,
  bucketInterval: BucketInterval,
  timezone: string,
  effectiveDateRange: { start: Date; end: Date },
) {
  const baselineBandsEnabled = Boolean(
    config.showBaselineBands &&
    config.baselineId &&
    config.aggregation === AGGREGATION_TYPE.AVG,
  )

  const { data: baselineRecord } = useBaselineDetail(
    config.metricId,
    config.baselineId,
    {
      enabled: baselineBandsEnabled,
      staleTime: 5 * 60 * 1000,
      gcTime: 15 * 60 * 1000,
    },
  )

  const { data: baselineHistory } = useBaselineHistory(
    {
      baselineId: config.baselineId ?? "",
      bucketInterval,
      timezone,
      startDate: effectiveDateRange.start.toISOString(),
      endDate: effectiveDateRange.end.toISOString(),
    },
    {
      enabled: baselineBandsEnabled,
      staleTime: 5 * 60 * 1000,
      gcTime: 15 * 60 * 1000,
    },
  )

  const baselineData = useMemo(
    () =>
      createChartBaselineData({
        baselineRecord: baselineRecord
          ? {
              ...baselineRecord,
              baseline_float: baselineRecord.baseline_float ?? null,
              baseline_sigma: baselineRecord.baseline_sigma ?? null,
            }
          : undefined,
        baselineHistory,
        baselineDirection: config.baselineDirection,
      }),
    [baselineRecord, baselineHistory, config.baselineDirection],
  )

  return baselineData
}
